import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { IoIosRemoveCircle } from "react-icons/io";
import { FaPlus } from 'react-icons/fa';
import { IoClose } from "react-icons/io5";
import './EditInvoice.css';

const MySwal = withReactContent(Swal);

// ---- Modal Styles (Second file ki UI) ----
const styles = {
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'auto',
    padding: '20px',
    animation: 'fadeIn 0.3s ease-in',
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    width: '95%',
    maxWidth: '900px',
    maxHeight: '90vh',
    overflow: 'auto',
    padding: '30px',
    position: 'relative',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    animation: 'slideIn 0.3s ease-out',
  },
  topBorder: {
    height: '35px',
    backgroundColor: '#004aad',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
    margin: '-30px -30px 20px -30px',
  },
  closeButton: {
    position: 'absolute',
    top: '4px',
    right: '10px',
    fontSize: '20px',
    fontWeight: 'bold',
    cursor: 'pointer',
    color: '#ffffff',
    background: 'transparent',
    border: 'none',
    zIndex: 999,
    padding: '2px 6px',
    lineHeight: '1',
    transition: 'all 0.2s ease',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '5px',
    paddingBottom: '0px',
  },
  invoiceHeading: {
    fontSize: '24px',
    color: '#262626',
    fontWeight: '600',
  },
  formSection: {
    marginBottom: '25px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#004aad',
    marginBottom: '8px',
    paddingBottom: '4px',
  },
  formGroup: {
    marginBottom: '15px',
  },
  formLabel: {
    fontWeight: '600',
    color: '#444',
    fontSize: '13px',
    marginBottom: '5px',
    display: 'block',
  },
  formInput: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e5e5e5',
    borderRadius: '4px',
    fontSize: '13px',
    color: '#333',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    boxSizing: 'border-box',
  },
  formInputFocus: {
    borderColor: '#004aad',
    boxShadow: '0 0 4px rgba(0,74,173,0.2)',
  },
  itemsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
    marginBottom: '20px',
  },
  tableHeader: {
    backgroundColor: '#f4f4f4',
    color: '#333',
    fontWeight: 'bold',
    height: '55px',
  },
  tableCell: {
    padding: '10px',
    textAlign: 'left',
  },
  textRight: {
    textAlign: 'right',
  },
  tableRowEven: {
    backgroundColor: '#f8f9fa',
  },
  actionButton: {
    padding: '12px 24px',
    backgroundColor: '#1e40af',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '14px',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(30, 64, 175, 0.2)',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  secondaryButton: {
    backgroundColor: '#ffffff',
    color: '#374151',
    border: '1px solid #d1d5db',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '16px',
    marginTop: '30px',
    paddingTop: '20px',
  },
  loadingError: {
    textAlign: 'center',
    marginTop: '20px',
    fontSize: '16px',
    padding: '20px',
    color: '#d9534f',
    backgroundColor: '#fff',
    borderRadius: '8px',
  },
  noItemsRow: {
    textAlign: 'center',
    color: '#777',
    padding: '15px',
  },
};

const animationStyles = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slideIn {
    from { transform: translateY(-30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

// Calculate rate from amount and quantity
const calculateRate = (quantity, amount) => {
  const qty = parseFloat(quantity) || 1;
  const amt = parseFloat(amount) || 0;
  return qty > 0 ? (amt / qty).toFixed(2) : '0.00';
};

// Process items data to include rate
const processItemsData = (items) => {
  return (items || []).map(item => ({
    ...item,
    rate: item.rate || calculateRate(item.quantity, item.amount),
    quantity: String(item.quantity || '1'),
    amount: String(parseFloat(item.amount || 0).toFixed(2))
  }));
};

const buildFormData = (invoiceData) => ({
  ...invoiceData,
  invoiceDate: invoiceData.invoiceDate
    ? new Date(invoiceData.invoiceDate).toISOString().split('T')[0]
    : '',
  items: processItemsData(invoiceData.items),
  additionalLines: invoiceData.additionalLines || [],
  shippingCost: String(invoiceData.shippingCost || ''),
  subtotal: String(parseFloat(invoiceData.subtotal || 0).toFixed(2)),
  taxes: String(parseFloat(invoiceData.taxes || 0).toFixed(2)),
  total: String(parseFloat(invoiceData.total || 0).toFixed(2)),
  paid: String(parseFloat(invoiceData.paid || 0).toFixed(2)),
  remaining: String((parseFloat(invoiceData.total || 0) - parseFloat(invoiceData.paid || 0)).toFixed(2)),
});

// ---- Real modal component ----
const EditInvoice = ({ isOpen, onClose, invoiceData: propInvoiceData, invoiceNumber, onSuccess }) => {
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Inject animation styles once
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = animationStyles;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  // Load form data whenever the modal is opened (or the invoice changes)
  useEffect(() => {
    if (!isOpen) return;

    const load = async () => {
      setError(null);
      try {
        let data = propInvoiceData;

        if (!data || String(data.invoiceNumber) !== String(invoiceNumber)) {
          setLoading(true);
          const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/invoices/${invoiceNumber}`);
          if (!response.data) throw new Error('Invoice not found');
          data = response.data;
        }

        setFormData(buildFormData(data));
      } catch (err) {
        console.error('Error fetching invoice data:', err);
        setError('Failed to load invoice data: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, invoiceNumber, propInvoiceData]);

  // Recalculate totals whenever items / additionalLines / taxes / paid change
  useEffect(() => {
    if (!formData) return;

    const itemsTotal = (formData.items || []).reduce(
      (sum, item) => sum + (parseFloat(item.amount) || 0), 0
    );
    const additionalLinesTotal = (formData.additionalLines || []).reduce(
      (sum, line) => sum + (parseFloat(line.amount) || 0), 0
    );
    const subtotal = itemsTotal + additionalLinesTotal;
    const taxes = parseFloat(formData.taxes) || 0;
    const total = subtotal + taxes;
    const paid = parseFloat(formData.paid) || 0;
    const remaining = total - paid;

    const nextSubtotal = subtotal.toFixed(2);
    const nextTotal = total.toFixed(2);
    const nextRemaining = remaining.toFixed(2);

    if (
      formData.subtotal !== nextSubtotal ||
      formData.total !== nextTotal ||
      formData.remaining !== nextRemaining
    ) {
      setFormData(prev => ({
        ...prev,
        subtotal: nextSubtotal,
        total: nextTotal,
        remaining: nextRemaining,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.items, formData?.additionalLines, formData?.taxes, formData?.paid]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedItems = [...prev.items];

      if (field === 'rate') {
        const quantity = parseFloat(updatedItems[index].quantity) || 0;
        const rate = parseFloat(value) || 0;
        updatedItems[index] = { ...updatedItems[index], rate: value, amount: (quantity * rate).toFixed(2) };
      } else if (field === 'quantity') {
        const quantity = parseFloat(value) || 0;
        const rate = parseFloat(updatedItems[index].rate) || 0;
        updatedItems[index] = { ...updatedItems[index], quantity: value, amount: (quantity * rate).toFixed(2) };
      } else {
        updatedItems[index] = { ...updatedItems[index], [field]: value };
      }

      return { ...prev, items: updatedItems };
    });
  };

  const handleAdditionalLineChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedLines = [...prev.additionalLines];
      updatedLines[index] = { ...updatedLines[index], [field]: value };
      return { ...prev, additionalLines: updatedLines };
    });
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: '1', amount: '0.00', rate: '0.00' }],
    }));
  };

  const addAdditionalLine = () => {
    setFormData((prev) => ({
      ...prev,
      additionalLines: [...prev.additionalLines, { description: '', amount: '0.00' }],
    }));
  };

  const removeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const removeAdditionalLine = (index) => {
    setFormData((prev) => ({
      ...prev,
      additionalLines: prev.additionalLines.filter((_, i) => i !== index),
    }));
  };

  const formatAmount = (value) => {
    const number = parseFloat(value);
    if (isNaN(number)) return "0.00";
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const requestData = {
        caseId: String(formData.caseId || ''),
        accountId: String(formData.accountId || ''),
        trackNumber: formData.trackNumber || undefined,
        shippingCost: parseFloat(formData.shippingCost) || 0,
        shipper: formData.shipper || undefined,
        rescheduleOption: Boolean(formData.rescheduleOption),
        invoiceNow: Boolean(formData.invoiceNow),
        relatedCase: formData.relatedCase || undefined,
        addShippingCost: Boolean(formData.addShippingCost),
        items: (formData.items || []).map(item => ({
          description: String(item.description || ''),
          quantity: parseFloat(item.quantity) || 0,
          amount: parseFloat(item.amount) || 0,
          rate: parseFloat(item.rate) || 0
        })),
        additionalLines: (formData.additionalLines || []).map(line => ({
          description: String(line.description || ''),
          amount: parseFloat(line.amount) || 0
        })),
        subtotal: parseFloat(formData.subtotal) || 0,
        taxes: parseFloat(formData.taxes) || 0,
        total: parseFloat(formData.total) || 0,
        invoiceDate: formData.invoiceDate || undefined,
        invoiceMessage: formData.invoiceMessage || undefined,
        printAfterSave: Boolean(formData.printAfterSave),
        status: ['paid', 'unpaid', 'overdue'].includes(formData.status) ? formData.status : 'unpaid',
        paid: parseFloat(formData.paid) || 0,
        remaining: parseFloat(formData.remaining) || 0
      };

      const cleanData = Object.fromEntries(
        Object.entries(requestData).filter(([_, v]) => v !== undefined)
      );

      const response = await axios.put(
        `https://hms.backend.hiragroupcloud.com/api/invoices/edit-invoices/${invoiceNumber}`,
        cleanData,
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data?.success) {
        await MySwal.fire({
          title: 'Success!',
          text: 'Invoice updated successfully!',
          icon: 'success',
          timer: 1800,
          timerProgressBar: true,
          showConfirmButton: false,
        });

        onSuccess?.(response.data.data);
        onClose?.();
      } else {
        throw new Error(response.data?.message || 'Failed to update invoice');
      }
    } catch (err) {
      console.error('Error updating invoice:', err);
      setError('Failed to update invoice: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  return (
    <div className="ei-overlay" style={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className="ei-modal" style={styles.modalContent}>
        <div className="ei-top" style={styles.topBorder}></div>
        <button
          className="ei-close"
          style={styles.closeButton}
          onClick={() => onClose?.()}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#004aad';
            e.currentTarget.style.backgroundColor = '#f4f4f4';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#ffffff';
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          ×
        </button>

        <div style={styles.headerContainer}>
          <div className="ei-heading" style={styles.invoiceHeading}>
            Edit Invoice #{invoiceNumber || 'N/A'}
          </div>
        </div>

        {error && <div style={styles.loadingError}>{error}</div>}

        {loading || !formData ? (
          <div style={styles.loadingError}>Loading...</div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Invoice Details */}
            <div className="ei-form-section" style={styles.formSection}>
              <div className="ei-section-title" style={styles.sectionTitle}>Invoice Details</div>
              <div
                className="ei-details-box"
                style={{
                  backgroundColor: "#f4f4f4",
                  padding: "15px",
                  borderTop: "2px solid #004aad",
                }}
              >
                <div
                  className="ei-details-flex"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      flex: 1,
                    }}
                  >
                    <label
                      style={{
                        fontWeight: "600",
                        fontSize: "14px",
                        minWidth: "80px",
                        color: "#333",
                      }}
                    >
                      Account
                    </label>
                    <span style={{ fontSize: "14px", color: "#333" }}>
                      {formData.accountId || "N/A"}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <label
                      style={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#333",
                      }}
                    >
                      Invoice Date
                    </label>
                    <input
                      className="ei-date-input"
                      type="date"
                      name="invoiceDate"
                      value={formData.invoiceDate || ''}
                      onChange={handleInputChange}
                      style={{
                        width: "170px",
                        padding: "8px 12px",
                        border: "1px solid #dcdcdc",
                        borderRadius: "4px",
                        fontSize: "13px",
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>

                {/* Extra Invoice fields - shipper, tracking, status, checkboxes, message */}
                {/*<div*/}
                {/*  style={{*/}
                {/*    display: "grid",*/}
                {/*    gridTemplateColumns: "1fr 1fr",*/}
                {/*    gap: "15px",*/}
                {/*    marginTop: "15px",*/}
                {/*  }}*/}
                {/*>*/}
                  {/*<div style={styles.formGroup}>*/}
                  {/*  <label style={styles.formLabel}>Case ID</label>*/}
                  {/*  <input*/}
                  {/*    type="text"*/}
                  {/*    name="caseId"*/}
                  {/*    value={formData.caseId || ''}*/}
                  {/*    onChange={handleInputChange}*/}
                  {/*    style={styles.formInput}*/}
                  {/*    onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}*/}
                  {/*    onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}*/}
                  {/*  />*/}
                  {/*</div>*/}

                  {/*<div style={styles.formGroup}>*/}
                  {/*  <label style={styles.formLabel}>Shipper</label>*/}
                  {/*  <input*/}
                  {/*    type="text"*/}
                  {/*    name="shipper"*/}
                  {/*    value={formData.shipper || ''}*/}
                  {/*    onChange={handleInputChange}*/}
                  {/*    style={styles.formInput}*/}
                  {/*    onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}*/}
                  {/*    onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}*/}
                  {/*  />*/}
                  {/*</div>*/}

                  {/*<div style={styles.formGroup}>*/}
                  {/*  <label style={styles.formLabel}>Tracking Number</label>*/}
                  {/*  <input*/}
                  {/*    type="text"*/}
                  {/*    name="trackNumber"*/}
                  {/*    value={formData.trackNumber || ''}*/}
                  {/*    onChange={handleInputChange}*/}
                  {/*    style={styles.formInput}*/}
                  {/*    onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}*/}
                  {/*    onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}*/}
                  {/*  />*/}
                  {/*</div>*/}

                  {/*<div style={styles.formGroup}>*/}
                  {/*  <label style={styles.formLabel}>Shipping Cost</label>*/}
                  {/*  <input*/}
                  {/*    type="number"*/}
                  {/*    step="0.01"*/}
                  {/*    name="shippingCost"*/}
                  {/*    value={formData.shippingCost || ''}*/}
                  {/*    onChange={handleInputChange}*/}
                  {/*    style={{ ...styles.formInput, textAlign: 'right' }}*/}
                  {/*    onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}*/}
                  {/*    onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}*/}
                  {/*  />*/}
                  {/*</div>*/}

                  {/*<div style={styles.formGroup}>*/}
                  {/*  <label style={styles.formLabel}>Status</label>*/}
                  {/*  <select*/}
                  {/*    name="status"*/}
                  {/*    value={formData.status || 'unpaid'}*/}
                  {/*    onChange={handleInputChange}*/}
                  {/*    style={styles.formInput}*/}
                  {/*    onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}*/}
                  {/*    onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}*/}
                  {/*  >*/}
                  {/*    <option value="unpaid">Unpaid</option>*/}
                  {/*    <option value="paid">Paid</option>*/}
                  {/*    <option value="overdue">Overdue</option>*/}
                  {/*  </select>*/}
                  {/*</div>*/}

                  {/*<div style={styles.formGroup}>*/}
                  {/*  <label style={styles.formLabel}>Paid</label>*/}
                  {/*  <input*/}
                  {/*    type="number"*/}
                  {/*    step="0.01"*/}
                  {/*    name="paid"*/}
                  {/*    value={formData.paid || '0.00'}*/}
                  {/*    onChange={handleInputChange}*/}
                  {/*    style={{ ...styles.formInput, textAlign: 'right' }}*/}
                  {/*    onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}*/}
                  {/*    onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}*/}
                  {/*  />*/}
                  {/*</div>*/}
                {/*</div>*/}

                {/*<div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px', marginTop: '12px' }}>*/}
                  {/*<label style={{ ...styles.formLabel, display: 'flex', alignItems: 'center', margin: 0 }}>*/}
                  {/*  <input*/}
                  {/*    type="checkbox"*/}
                  {/*    name="rescheduleOption"*/}
                  {/*    checked={formData.rescheduleOption || false}*/}
                  {/*    onChange={handleInputChange}*/}
                  {/*    style={{ marginRight: '8px' }}*/}
                  {/*  />*/}
                  {/*  Reschedule Option*/}
                  {/*</label>*/}
                  {/*<label style={{ ...styles.formLabel, display: 'flex', alignItems: 'center', margin: 0 }}>*/}
                  {/*  <input*/}
                  {/*    type="checkbox"*/}
                  {/*    name="invoiceNow"*/}
                  {/*    checked={formData.invoiceNow || false}*/}
                  {/*    onChange={handleInputChange}*/}
                  {/*    style={{ marginRight: '8px' }}*/}
                  {/*  />*/}
                  {/*  Invoice Now*/}
                  {/*</label>*/}
                  {/*<label style={{ ...styles.formLabel, display: 'flex', alignItems: 'center', margin: 0 }}>*/}
                  {/*  <input*/}
                  {/*    type="checkbox"*/}
                  {/*    name="addShippingCost"*/}
                  {/*    checked={formData.addShippingCost || false}*/}
                  {/*    onChange={handleInputChange}*/}
                  {/*    style={{ marginRight: '8px' }}*/}
                  {/*  />*/}
                  {/*  Add Shipping Cost*/}
                  {/*</label>*/}
                  {/*<label style={{ ...styles.formLabel, display: 'flex', alignItems: 'center', margin: 0 }}>*/}
                  {/*  <input*/}
                  {/*    type="checkbox"*/}
                  {/*    name="printAfterSave"*/}
                  {/*    checked={formData.printAfterSave || false}*/}
                  {/*    onChange={handleInputChange}*/}
                  {/*    style={{ marginRight: '8px' }}*/}
                  {/*  />*/}
                  {/*  Print After Save*/}
                  {/*</label>*/}
                {/*</div>*/}
              </div>
            </div>

            {/* Invoice Procedures (Items) */}
            <div className="ei-form-section" style={styles.formSection}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <div className="ei-section-title" style={styles.sectionTitle}>Invoice Procedures</div>
                <button
                  type="button"
                  className="ei-plus-btn"
                  onClick={addItem}
                  style={{
                    width: "38px",
                    height: "38px",
                    border: "none",
                    borderRadius: "4px",
                    backgroundColor: "#004aad",
                    color: "#fff",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                  }}
                >
                  <FaPlus />
                </button>
              </div>
              <div className="ei-scroll">
              <table className="ei-items-table" style={styles.itemsTable}>
                <thead>
                  <tr style={styles.tableHeader}>
                    <th style={{ ...styles.tableCell, width: '38%' }}>Description</th>
                    <th style={{ ...styles.tableCell, ...styles.textRight, width: '12%' }}>Qty</th>
                    <th style={{ ...styles.tableCell, ...styles.textRight, width: '20%' }}>Rate</th>
                    <th style={{ ...styles.tableCell, ...styles.textRight, width: '20%' }}>Amount</th>
                    <th style={{ ...styles.tableCell, ...styles.textRight, width: '10%' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(formData.items || []).map((item, index) => (
                    <tr
                      key={index}
                      style={{
                        ...(index % 2 !== 0 ? styles.tableRowEven : {}),
                        borderBottom: "1px solid #dcdcdc",
                      }}
                    >
                  <td style={styles.tableCell}>
  <div
    className="ei-desc-cell"
    style={{
      padding: "8px 12px",
      minHeight: "38px",
      display: "flex",
      alignItems: "center",
      fontSize: "14px",
      color: "#333",
    }}
  >
    {item.description || "-"}
  </div>
</td>
                      <td style={{ ...styles.tableCell, ...styles.textRight }}>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={item.quantity || '0'}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          style={{ ...styles.formInput, textAlign: 'right' }}
                          onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                          onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                        />
                      </td>
                  <td style={{ ...styles.tableCell, ...styles.textRight }}>
  <div
    className="ei-rate-cell"
    style={{
      padding: "8px 12px",
      minHeight: "38px",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      fontSize: "14px",
      color: "#333",
    }}
  >
    {formatAmount(item.rate)}
  </div>
</td>
                      <td style={{ ...styles.tableCell, ...styles.textRight }}>
                        <input
                          type="number"
                          step="0.01"
                          value={item.amount || '0.00'}
                          readOnly
                          style={{ ...styles.formInput, textAlign: 'right', backgroundColor: '#f8f9fa' }}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, ...styles.textRight }}>
                        <button
                          type="button"
                          className="ei-remove-btn"
                          style={{
                            color: '#d9534f',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '20px',
                            padding: '8px',
                            borderRadius: '50%',
                            transition: 'all 0.3s ease',
                            outline: 'none',
                          }}
                          onClick={() => removeItem(index)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#c9302c';
                            e.currentTarget.style.transform = 'scale(1.2)';
                            e.currentTarget.style.textShadow = '0 0 8px rgba(217, 83, 79, 0.9)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#d9534f';
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.textShadow = 'none';
                          }}
                        >
                          <IoIosRemoveCircle style={{ transition: 'all 0.3s ease' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(!formData.items || formData.items.length === 0) && (
                    <tr>
                      <td colSpan="5" style={{ ...styles.tableCell, textAlign: 'center', color: '#777' }}>
                        No items in this invoice.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>

            {/* Additional Lines */}
            <div className="ei-form-section" style={styles.formSection}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <div className="ei-section-title" style={styles.sectionTitle}>Additional Lines</div>
                <button
                  type="button"
                  className="ei-plus-btn"
                  onClick={addAdditionalLine}
                  style={{
                    width: "38px",
                    height: "38px",
                    border: "none",
                    borderRadius: "4px",
                    backgroundColor: "#004aad",
                    color: "#fff",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                  }}
                >
                  <FaPlus />
                </button>
              </div>
              <div className="ei-scroll">
              <table className="ei-lines-table" style={styles.itemsTable}>
                <thead>
                  <tr style={styles.tableHeader}>
                    <th style={{ ...styles.tableCell, width: '70%' }}>Description</th>
                    <th style={{ ...styles.tableCell, ...styles.textRight, width: '20%' }}>Amount</th>
                    <th style={{ ...styles.tableCell, ...styles.textRight, width: '10%' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(formData.additionalLines || []).map((line, index) => (
                    <tr key={index} style={index % 2 === 0 ? {} : styles.tableRowEven}>
                      <td style={{ ...styles.tableCell, borderBottom: "1px solid #dcdcdc" }}>
                        <input
                          type="text"
                          value={line.description || ''}
                          onChange={(e) => handleAdditionalLineChange(index, 'description', e.target.value)}
                          style={styles.formInput}
                          onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                          onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, ...styles.textRight }}>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={line.amount ?? ""}
                          onChange={(e) => handleAdditionalLineChange(index, 'amount', e.target.value)}
                          style={{ ...styles.formInput, textAlign: 'right' }}
                          onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                          onBlur={(e) => {
                            Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' });
                            const amount = parseFloat(line.amount);
                            if (!isNaN(amount)) {
                              handleAdditionalLineChange(index, 'amount', amount.toFixed(2));
                            }
                          }}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, ...styles.textRight }}>
                        <button
                          type="button"
                          className="ei-remove-btn"
                          style={{
                            color: '#d9534f',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '20px',
                            padding: '8px',
                            borderRadius: '50%',
                            transition: 'all 0.3s ease',
                            outline: 'none',
                          }}
                          onClick={() => removeAdditionalLine(index)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#c9302c';
                            e.currentTarget.style.transform = 'scale(1.2)';
                            e.currentTarget.style.textShadow = '0 0 8px rgba(217, 83, 79, 0.9)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#d9534f';
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.textShadow = 'none';
                          }}
                        >
                          <IoIosRemoveCircle style={{ transition: 'all 0.3s ease' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(!formData.additionalLines || formData.additionalLines.length === 0) && (
                    <tr>
                      <td colSpan="3" style={{ ...styles.tableCell, textAlign: 'center', color: '#777' }}>
                        No additional lines.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>

            {/* Note + Summary */}
            <div className="ei-form-section" style={styles.formSection}>
              <div
                className="ei-note-summary"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "30px",
                  alignItems: "flex-start",
                }}
              >
                <div className="ei-note-wrap" style={{ width: "420px" }}>
                  <div className="ei-section-title" style={styles.sectionTitle}>Note</div>
                  <textarea
                    className="ei-note-textarea"
                    name="invoiceMessage"
                    value={formData.invoiceMessage || ""}
                    onChange={handleInputChange}
                    style={{
                      width: "240px",
                      minHeight: "50px",
                      border: "1px solid #dcdcdc",
                      borderRadius: "6px",
                      padding: "10px",
                      fontSize: "14px",
                      resize: "vertical",
                    }}
                  />
                </div>

                <div className="ei-summary" style={{ width: "300px" }}>
                  {[
                    ["Subtotal", formData.subtotal],
                    ["Taxes", formData.taxes],
                    ["Total", formData.total],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "10px 0",
                      }}
                    >
                      <span style={{ fontWeight: "600" }}>{label}</span>
                      <span>{formatAmount(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="ei-btn-group" style={styles.buttonGroup}>
              <button
                type="button"
                className="ei-btn"
                style={{ ...styles.actionButton, ...styles.secondaryButton }}
                onClick={() => onClose?.()}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                  e.currentTarget.style.boxShadow = '0 3px 6px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="ei-btn"
                style={styles.actionButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1e3a8a';
                  e.currentTarget.style.boxShadow = '0 3px 6px rgba(30, 58, 138, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#1e40af';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(30, 64, 175, 0.2)';
                }}
              >
                Update Invoice
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EditInvoice;